import type { RefObject } from "react";

type PronunciationGuideProps = {
  open: boolean;
  onClose: () => void;
  dialogRef: RefObject<HTMLElement | null>;
  word: string;
  pronunciation: string;
  ipa?: string;
  onHear: () => void;
};

export function PronunciationGuide({
  open,
  onClose,
  dialogRef,
  word,
  pronunciation,
  ipa,
  onHear,
}: PronunciationGuideProps) {
  if (!open) return null;

  const stressedPart = pronunciation.match(/[A-Z]+(?:-[A-Z]+)*/u)?.[0] || "the capitalised part";

  return (
    <div className="pronunciation-guide-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="pronunciation-guide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pronunciation-guide-title"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <span className="section-kicker">Pronunciation guide</span>
          <button type="button" onClick={onClose} aria-label="Close pronunciation guide">×</button>
        </header>
        <h2 id="pronunciation-guide-title">How to read {word || "this word"}.</h2>
        <section className="pronunciation-guide-current" aria-label={`Current pronunciation for ${word || "this word"}`}>
          <div>
            <span>Current word</span>
            <strong>{word || "This word"}</strong>
            <p>{pronunciation || "Use Hear while a readable written pronunciation is being prepared."}</p>
          </div>
          <button className="speak-button" type="button" onClick={onHear} aria-label={`Hear ${word || "this word"} pronounced`}>
            <span aria-hidden="true">▶</span> Hear
          </button>
        </section>
        <div className="pronunciation-guide-grid">
          <section>
            <strong>{stressedPart}</strong>
            <p>Capital letters mark the stressed part. Say {stressedPart} a little more clearly.</p>
          </section>
          <section>
            <strong>Hyphens</strong>
            <p>They separate the spoken parts of a word. They are not pauses.</p>
          </section>
          <section>
            <strong>{ipa ? `/${ipa.replace(/^\/+|\/+$/gu, "")}/` : "IPA"}</strong>
            <p>IPA is the precise sound alphabet. Keep it as a reference; begin with the plain pronunciation above.</p>
          </section>
          <section>
            <strong>Hear</strong>
            <p>The voice comes from your device. Its accent may differ from the written guide.</p>
          </section>
        </div>
        <p className="pronunciation-guide-note">
          A pronunciation can vary by accent. Verbalore shows the maintained evidence it has and does not pretend that one accent is the only correct one.
        </p>
        <button className="primary-action" type="button" onClick={onClose}>Return to this word</button>
      </section>
    </div>
  );
}
